import getCorrespondingDuration from 'core/services/getPlansByProductId/getCorrespondingDuration'
import getCorrespondingPolicy from 'core/services/getPlansByProductId/getCorrespondingPolicy'
import sortByMonthly from 'core/services/getPlansByProductId/sortByMonthly'
import { moveFirstByIndex, findFirstOccurrence, propAccessor } from '@hc-frontend/utils'
import orderPlansByRecEngine from '../orderPlansByRecEngine'
import { IS_AGENT } from 'core/constants/environment'
import isEpicPlan from 'utils/pivot/plan/isEpicPlan'
import isPlanPPO from 'utils/pivot/plan/isPlanPPO'
import {
  AMERITAS_DENTAL,
  AMERITAS_VISION,
  DENTAL,
  EPIC,
  LATITUDE,
  SURE_CARE_NAME,
  VISION
} from 'core/constants/products'
import curry from 'utils/functional/curry'
import alphabeticalSort from 'utils/alphabeticalSort'
import getById from 'utils/DOM/getById'
import parseFeaturedPlans from '../utils/parseFeaturedPlans'
import getPlanTypeKey from 'core/controllerPlans/getPlanTypeKey'
import sortByPlanType from 'core/services/getPlansByProductId/sortByPlanType'
import getValidatedAddons from '../utils/getValidatedAddons'
import isArray from 'utils/validation/isArray'
import money from 'utils/format/money'
import getPlanFiltersAdditionalInfo from './getPlanFiltersAdditionalInfo'
import applyFilters from './applyFilters'
import getAndValidatePeriod from 'utils/pivot/plan/getAndValidateDuration'
import getValidDuration from 'utils/pivot/plan/getValidDuration'
import updatePlanPrices from './updatePlanPrices'
import ascSort from 'utils/ascSort'

const toMoney = money

function getRanges(store) {
  const result = {
    '0-100': 0,
    '101-200': 0,
    '201-300': 0,
    '301-400': 0,
    '401-Infinity': 0
  }

  for (const id of store.plansToShow) {
    const premium =
      store.plans[id].type === SURE_CARE_NAME &&
      store.hcStorage.location.state_acronym === 'VA'
        ? store.plans[id].durationsArray[0].monthly.premium
        : +getCorrespondingPolicy({
            plan: store.plans[id],
            mainPeriod: store.period,
            additionalPeriods: store.additionalPeriods
          }).monthly.premium

    if (premium < 101) {
      result['0-100'] += 1
    } else if (premium < 201) {
      result['101-200'] += 1
    } else if (premium < 301) {
      result['201-300'] += 1
    } else if (premium < 401) {
      result['301-400'] += 1
    } else {
      result['401-Infinity'] += 1
    }
  }

  return result
}

const premiumFilter = (ranges, period, additionalPeriods, plan) => {
  const node = getById(`${plan.planId}-extended`)
  const durations = getCorrespondingDuration({
    plan,
    mainPeriod: period,
    additionalPeriods
  })
  const maxDuration = Math.max(...Object.keys(durations).map(k => +k))
  const policyToUse = node && node.checked ? durations[maxDuration] : durations['1']
  const premium = policyToUse.monthly.premium

  for (const { min, max } of ranges) {
    if (min <= premium && (!max || premium <= max)) {
      return true
    }
  }

  return false
}

const drugFilter = (drugs, plan) => drugs.includes(plan.drug)
const networkFilter = (items, plan) => items.includes(plan.coreBenefits.networkProvider)

const periodFilter = (period, policies, filterOptionsByProductType, hcStorage, plan) => {
  const planType = getPlanTypeKey(plan)
  const value = `${policies > 1 ? `${policies}x` : ''}${period}`

  const periodValues = propAccessor<Array<string>>(filterOptionsByProductType, [
    planType,
    'period',
    'values'
  ])

  return periodValues != null && periodValues.length
    ? periodValues.includes(value)
    : false
}

const arrayFilter = (items, name, plan) => items.includes(plan[name])
const arrayFilterCoreBenefits = (items, name, plan) =>
  items.includes(plan.coreBenefits[name])
const hasBenefit = (filterBenefits, plan) => {
  return filterBenefits.every(benefit => plan.benefits.indexOf(benefit) >= 0)
}

const rangeListFilter = ({ min }, key, filterOptionsByProductType, plan) => {
  for (const value of filterOptionsByProductType[plan.planTypeKey][key].values) {
    if (min.includes(String(value))) {
      return true
    }
  }
  return false
}

function getBaseFiltersToApply({
  filters,
  filterOptionsByProductType,
  period,
  hcStorage,
  additionalPeriods
}) {
  return [
    filters.type.length && curry(arrayFilter)(filters.type, 'type'),
    filters.price.length &&
      curry(premiumFilter)(filters.price, period, additionalPeriods),
    filters.coverage.max &&
      filters.coverage.max.length &&
      curry(rangeListFilter)(filters.coverage, 'coverage', filterOptionsByProductType),
    filters.networkProvider.length && curry(networkFilter)(filters.networkProvider),
    filters.deductible.max &&
      filters.deductible.max.length &&
      curry(rangeListFilter)(
        filters.deductible,
        'deductible',
        filterOptionsByProductType
      ),
    filters.coinsurance.length &&
      curry(arrayFilterCoreBenefits)(filters.coinsurance, 'userCoinsurance'),
    filters.moop.length && curry(arrayFilterCoreBenefits)(filters.moop, 'outOfPocketMax'),
    filters.drug.length && curry(drugFilter)(filters.drug),
    filters.period &&
      curry(periodFilter)(
        filters.period,
        filters.policies,
        filterOptionsByProductType,
        hcStorage
      ),
    filters.benefits.length && curry(hasBenefit)(filters.benefits)
  ].filter(Boolean)
}

const getAllFilters = (store, plansToShow) => {
  const { filtersByProductType, planFiltersToApply, filterOptionsByProductType } =
    getPlanFiltersAdditionalInfo({
      filterOptionsByProductType: store.filterOptionsByProductType,
      filtersByProductType: store.filtersByProductType,
      plansToShow,
      plans: store.plans,
      policies: store.policies || store.hcStorage.policies,
      period: store.period || store.defaultPeriod || store.hcStorage.period
    })

  store.filtersByProductType = filtersByProductType
  store.filterOptionsByProductType = filterOptionsByProductType

  return [...getBaseFiltersToApply(store), ...planFiltersToApply].filter(Boolean)
}

const checkForInitFilters = store => {
  if (propAccessor(store, 'hcStorage.location.state_acronym') === 'ND') {
    const period = store.filters.period || store.period
    let policies = store.filters.policies || store.policies
    if (!policies && period === 180) {
      policies = 2
    }

    return {
      ...store.filters,
      period,
      policies
    }
  }

  return store.filters
}

function applyRules(store, externalConfig = {}) {
  if (!store.period) {
    store.period = store.filters.period || store.defaultPeriod
  }
  store.filters = checkForInitFilters(store)
  store.prices = updatePlanPrices(store, store.planId)

  let plansToShow = store.plansSTM
    .slice()
    .sort(
      curry(sortByMonthly)(
        ascSort,
        store.plans,
        store.additionalPeriods
          ? [store.period, ...store.additionalPeriods]
          : store.period
      )
    )

  store.recommended = []
  if (store.showRecommend && store.recEngine) {
    const periods = store.additionalPeriods
      ? [store.period, ...store.additionalPeriods]
      : [store.period]

    store.recommended = orderPlansByRecEngine({
      recEngine: store.recEngine,
      plans: store.plans,
      plansSTM: plansToShow,
      periods
    }).splice(0, 3)
  }

  store.recommendDurations = parseFeaturedPlans({
    store
  })

  plansToShow = plansToShow.filter(
    id =>
      applyFilters(getAllFilters(store, plansToShow), store.plans[id]) &&
      !store.plans[id].multiCarrierPlan
  )

  if (IS_AGENT && store.plansByType[EPIC] && store.plansByType[EPIC].length) {
    let hasPPO = false
    let indexBase

    for (let index = 0; index < plansToShow.length; index++) {
      const plan = store.plans[plansToShow[index]]

      if (isEpicPlan(plan)) {
        if (!isPlanPPO(plan) && plan.coreBenefits.deductible === '5000') {
          hasPPO = true
          plansToShow = moveFirstByIndex(plansToShow, index)
          break
        } else if (plan.coreBenefits.deductible === '5000') {
          indexBase = index
        }
      }
    }

    if (!hasPPO && indexBase) {
      plansToShow = moveFirstByIndex(plansToShow, indexBase)
    }
  }

  store.plansToShow = plansToShow.sort(
    curry(sortByPlanType)(alphabeticalSort, store.plans)
  )
  store.showRecommend = false
  store.plansToCompare = store.plansToCompare || []
  store.plansVisited = store.plansVisited || []
  store.countRanges = getRanges(store)

  const validatedAddonsByPlan = getValidatedAddons(store)

  store.latitudeAddons = {}
  store.dentalAddons = {}
  store.visionAddons = {}

  store.addonsByPlan = validatedAddonsByPlan
  let isAddonApplied = false
  Object.keys(store.addonsByPlan).forEach(planId => {
    const planAddons = store.addonsByPlan[planId].filter(addonId => {
      const addon = store.plans[addonId]
      return addon && isArray(addon.durations)
    })
    const plan = store.plans[planId]

    store.latitudeAddons[planId] = findFirstOccurrence(
      planAddons,
      store.plansByType[LATITUDE]
    )
    store.dentalAddons[planId] = findFirstOccurrence(
      planAddons,
      (store.plansByType[DENTAL] || []).concat(store.plansByType[AMERITAS_DENTAL] || [])
    )
    store.visionAddons[planId] = findFirstOccurrence(
      planAddons,
      (store.plansByType[VISION] || []).concat(store.plansByType[AMERITAS_VISION] || [])
    )

    if (plan) {
      const planKey = getPlanTypeKey(plan)
      const { period, policies } = store.filtersByProductType[planKey]

      const validatedPlanDurations = getAndValidatePeriod(plan, period)

      let duration: Duration | undefined

      if (plan?.multiCarrierPlan) {
        duration = validatedPlanDurations
      } else {
        duration = getValidDuration(validatedPlanDurations, policies)
      }

      const planCost = duration.monthly.premium

      const planTitle = planAddons.reduce(
        (accum, current) => `${accum} + ${store.plans[current].planName}`,
        plan.planName
      )
      const addonsCost = planAddons.reduce((accum, current) => {
        const plan = store.plans[current]
        return accum + ((plan && plan.durations[0].monthly.premium) || 0)
      }, 0)

      const visionPlan = store.visionAddons[planId]
      const dentalPlan = store.dentalAddons[planId]
      
      plan.cost = toMoney(planCost + addonsCost)
      plan.title = planTitle

      isAddonApplied = true

      plan.dentalVisionTitle = [dentalPlan, visionPlan]
        .filter(Boolean)
        .map(addonId => store.plans[addonId].planName)
        .join(' + ')
    }

    store.plans[planId] = plan
  })

  //change that logic to appy external plan selected only when externalConfig.selectedPlanId is present
  if (externalConfig.selectedPlanId) {
    const plan = store.plans[externalConfig.selectedPlanId]

    let cost = '', title = ''
    if (plan) {
      const planKey = getPlanTypeKey(plan)
      const { period, policies } = store.filtersByProductType[planKey]

      const validatedPlanDurations = getAndValidatePeriod(plan, period)

      let duration: Duration | undefined

      if (plan?.multiCarrierPlan) {
        duration = validatedPlanDurations
      } else {
        duration = getValidDuration(validatedPlanDurations, policies)
      }
      cost =  isAddonApplied ? plan.cost : toMoney(duration.monthly.premium)

      title = isAddonApplied ? plan.title : plan.planName
    }

    const externalPlan = store.externalPlans.find(
      plan => plan.id === store.externalPlanSelected
    )
  
    const price = externalPlan?.products.reduce((acc, product) => acc + product.price, 0) ?? 0
    const planCost = parseFloat(cost.replace('$', '')) + price
    plan.cost =  toMoney(planCost)
    plan.title = `${title}${externalPlan?.name ? ` + ${externalPlan.name}` : ''}`
  }

  return store
}

export default applyRules
